// MCP (Model Context Protocol) postMessage gateway.
//
// ClayForge is a static Netlify SPA — there's no Node server inside the page
// where a stdio MCP server can run. This module exposes the same operations
// (graph mutations) via window.postMessage so an external MCP server (running
// in Node/Electron) can iframe ClayForge or open it in a tab and drive it.
//
// External agents can also post messages directly from a parent window when
// embedding ClayForge as an iframe — e.g. Claude Code can spawn a wrapper
// process that bridges its stdio MCP transport to a hidden browser running
// this page, forwarding tool calls as postMessage requests.
//
// PROTOCOL:
//   Inbound  (parent → ClayForge):
//     { source: 'clayforge-mcp', id: string, method: string, params: any }
//   Outbound (ClayForge → parent):
//     { source: 'clayforge-mcp', id: string, ok: boolean, result?: any, error?: string }
//
// Methods mirror the LLM tool surface (graphTools.ts dispatchTool): list_node_types,
// get_graph, add_node, connect_wire, set_param, delete_node, clear_graph.

import { dispatchTool } from './graphTools';

const SOURCE = 'clayforge-mcp';

interface InMsg {
  source: string;
  id: string;
  method: string;
  params?: Record<string, unknown>;
}

let installed = false;

export function installMcpGateway(): void {
  if (installed) return;
  installed = true;
  window.addEventListener('message', async (e: MessageEvent) => {
    const data = e.data as InMsg | undefined;
    if (!data || data.source !== SOURCE || !data.id || !data.method) return;
    try {
      const result = await dispatchTool(data.method, data.params ?? {});
      respond(e.source, { source: SOURCE, id: data.id, ok: true, result });
    } catch (err) {
      respond(e.source, { source: SOURCE, id: data.id, ok: false, error: (err as Error).message });
    }
  });
  // Hello signal so a wrapping host can detect that the bridge is live.
  // Posts to opener (when window.open'd) and to parent (when iframe'd).
  const hello = { source: SOURCE, event: 'ready', version: 1 };
  if (window.opener) window.opener.postMessage(hello, '*');
  if (window.parent && window.parent !== window) window.parent.postMessage(hello, '*');
}

function respond(target: MessageEventSource | null, payload: unknown): void {
  if (!target) return;
  // MessageEventSource lacks origin info reliably across browsers; '*' is OK
  // because the response only contains the result of an op the host requested.
  (target as Window).postMessage(payload, { targetOrigin: '*' } as WindowPostMessageOptions);
}
